import React, { ReactElement } from 'react'
import styled from '@emotion/styled'
import { RecipeWithQuantity } from '@mindfulchefuk/features/Recipes/interfaces'
import { Box, Grid, Modal, ModalHeader } from '@mindfulchefuk/design-system'
import { closeModal } from '@mindfulchefuk/utils/modalUtils'
import { logModalClose } from '@mindfulchefuk/features/Onboarding/utils/onboardingAnalytics'
import { THandleIncreaseOrDecrement } from '@mindfulchefuk/onboarding/types'
import { RecipeModalTitle } from '@mindfulchefuk/features/Recipes/components/RecipeModal/RecipeModalTitle'
import { RecipeModalLeftSide } from '@mindfulchefuk/features/Recipes/components/RecipeModal/RecipeModalLeftSide'
import { RecipeModalTrustpilotFooter } from '@mindfulchefuk/features/Recipes/components/RecipeModal/RecipeModalTrustpilotFooter'
import { transformExpandedAvailabilityToRecipeWithQuantity } from '@mindfulchefuk/onboarding/helpers/dataTransforms/transformRecipeAvailabilitiesToRecipeWithQuantity'
import { OnboardingRecipeModalRightSide } from '@mindfulchefuk/onboarding/components/OnboardingRecipeModal/OnboardingRecipeModalRightSide/OnboardingRecipeModalRightSide'
import Loader from '@mindfulchefuk/components/Loader'
import { useGetRecipeAvailabilityById } from '@mindfulchefuk/hooks/useGetRecipeAvailabilityById'
import { useAtomValue } from 'jotai'
import { recipesInBasketAtom } from '@mindfulchefuk/store/onboarding'

export type TOnboardingRecipeModal = {
  availabilityId: string
  isAdditionDisabled: boolean
  onDecrement: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<RecipeWithQuantity>) => void
  onIncrement: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<RecipeWithQuantity>) => void
}

const HeaderWrapper = styled('div')`
  @media screen and (min-width: 640px) {
    display: none;
  }
`

export const OnboardingRecipeModal = ({
  availabilityId,
  isAdditionDisabled,
  onDecrement,
  onIncrement,
}: TOnboardingRecipeModal): ReactElement => {
  const recipesInBasket = useAtomValue(recipesInBasketAtom)
  const { data, isLoading } = useGetRecipeAvailabilityById(availabilityId)

  const handleRecipeModalClose = () => {
    closeModal('recipe')
    logModalClose()
  }

  if (!availabilityId) {
    return null
  }

  const recipe =
    data &&
    transformExpandedAvailabilityToRecipeWithQuantity({
      recipeAvailability: data,
      recipesInBasket,
    })

  return (
    <Modal size="large" closeModal={handleRecipeModalClose}>
      {isLoading && (
        <Box minHeight="200px">
          <Loader />
        </Box>
      )}
      {recipe && (
        <>
          <ModalHeader closeModal={handleRecipeModalClose}>
            <HeaderWrapper>
              <RecipeModalTitle title={recipe.title} />
            </HeaderWrapper>
          </ModalHeader>
          <Grid
            templateColumns={['1fr', '1fr 1fr']}
            color="aubergine"
            columnGap={12}
          >
            <RecipeModalLeftSide recipe={recipe} />
            <OnboardingRecipeModalRightSide
              isAdditionDisabled={isAdditionDisabled}
              recipe={recipe}
              onDecrement={() => onDecrement({ recipe, clickOrigin: 'modal' })}
              onIncrement={() => onIncrement({ recipe, clickOrigin: 'modal' })}
            />
          </Grid>
        </>
      )}
      <RecipeModalTrustpilotFooter />
    </Modal>
  )
}
